import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, RefreshCw, FileText, AlertTriangle, RotateCcw, Coins, Building2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Mini-tuile KPI alignée sur le style des autres KpiCard du cockpit.
 * Width: même grille (lg:grid-cols-6) — height: compact.
 */
const MiniKpi = ({ icon: Icon, label, value, sub, accent = 'emerald', onClick, testId, alert }) => {
  const colors = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'text-amber-600' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'text-rose-600' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-600' },
    zinc:    { bg: 'bg-zinc-50',    text: 'text-zinc-700',    icon: 'text-zinc-500' },
  };
  const c = colors[accent] || colors.zinc;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white border border-zinc-200 rounded-xl p-3 hover:shadow-sm transition-all ${alert ? 'ring-1 ring-rose-300' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 truncate">{label}</span>
        <div className={`p-1 rounded ${c.bg}`}>
          <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
        </div>
      </div>
      <div className={`text-xl font-bold ${c.text} leading-tight`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{sub}</div>}
    </button>
  );
};

const FneComplianceWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/fne-billing/cockpit-kpi`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return null;

  return (
    <div className="space-y-2" data-testid="fne-compliance-widget">
      {/* Header strip — compact, aligné cockpit */}
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
          <span className="text-xs font-semibold text-zinc-700">Conformité FNE</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
            data.health === 'green' ? 'bg-emerald-100 text-emerald-700' :
            data.health === 'amber' ? 'bg-amber-100 text-amber-700' :
            'bg-rose-100 text-rose-700'
          }`}>
            {data.health === 'green' ? 'CONFORME' : data.health === 'amber' ? 'ATTENTION' : 'CRITIQUE'}
          </span>
          <span className="text-[10px] text-zinc-400">· {data.fneActiveClients} client(s) actifs</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={refreshing} className="text-zinc-400 hover:text-zinc-600" data-testid="fne-widget-refresh">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <Link to="/admin/fne-invoices" className="text-[10px] text-blue-600 hover:underline">Détail →</Link>
        </div>
      </div>

      {/* 6 mini-KPI tiles dans la même grille que le cockpit */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniKpi
          icon={Building2}
          label="Readiness FNE"
          value={data.readiness ? `${data.readiness.ready}/${data.readiness.total}` : '—'}
          sub={data.readiness ? `${data.readiness.percentage}% prêtes pour PROD` : ''}
          accent={!data.readiness ? 'zinc' : data.readiness.ok ? 'emerald' : data.readiness.percentage >= 50 ? 'amber' : 'rose'}
          alert={data.readiness && !data.readiness.ok}
          onClick={() => navigate('/admin/clients?tab=companies&readiness=incomplete')}
          testId="kpi-fne-readiness"
        />
        <MiniKpi
          icon={ShieldCheck}
          label="Couverture J-30"
          value={`${data.coverage.percentage}%`}
          sub={`${data.coverage.certifiedCount}/${data.coverage.eligibleTotal} certifiées`}
          accent={data.coverage.ok ? 'emerald' : data.coverage.percentage >= 80 ? 'amber' : 'rose'}
          alert={!data.coverage.ok}
          testId="kpi-fne-coverage"
        />
        <MiniKpi
          icon={FileText}
          label="Non-certifiées"
          value={data.notCertifiedCount.value}
          sub="Factures éligibles"
          accent={data.notCertifiedCount.ok ? 'emerald' : 'rose'}
          alert={!data.notCertifiedCount.ok}
          testId="kpi-fne-not-certified"
        />
        <MiniKpi
          icon={AlertTriangle}
          label="Sans NCC"
          value={data.missingNcc.value}
          sub="Clients FNE actifs"
          accent={data.missingNcc.ok ? 'emerald' : 'rose'}
          alert={!data.missingNcc.ok}
          testId="kpi-fne-missing-ncc"
        />
        <MiniKpi
          icon={RotateCcw}
          label="Avoirs en attente"
          value={data.refundsPending.value}
          sub="Depuis > 7 jours"
          accent={data.refundsPending.ok ? 'emerald' : 'amber'}
          alert={!data.refundsPending.ok}
          testId="kpi-fne-refunds-pending"
        />
        <MiniKpi
          icon={Coins}
          label="Stickers DGI"
          value={data.stickerBalance.value ?? '—'}
          sub={`Seuil: ${data.stickerBalance.threshold ?? '?'}`}
          accent={data.stickerBalance.ok ? 'emerald' : 'rose'}
          alert={!data.stickerBalance.ok}
          testId="kpi-fne-sticker-balance"
        />
      </div>
    </div>
  );
};

export default FneComplianceWidget;
